'use client';

import { useObservable } from '@legendapp/state/react';
import { $React } from '@legendapp/state/react-web';
import { type DetailedHTMLProps, type HTMLAttributes, useEffect, useRef } from 'react';
import type { Breakpoint } from '@/lib/breakpoints';
import { cn } from '@/lib/cn';

export interface ReadMoreTextProps extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
    lines?: number;
    containerClassName?: string;
    readMoreClassName?: string;
    readMoreLabel?: string;
    readLessLabel?: string;
    breakpoints?: Partial<Record<Breakpoint, number>>;
}

export function ReadMoreText({
    className,
    children,
    lines = 3,
    containerClassName,
    readMoreClassName,
    readMoreLabel = 'Read more',
    readLessLabel = 'Hide more',
    breakpoints,
    ...props
}: ReadMoreTextProps) {
    const isTruncated$ = useObservable(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const element = containerRef.current;
        if (!element) {
            return;
        }

        const checkTruncation = () => {
            element.classList.remove('group-has-checked:line-clamp-none');
            isTruncated$.set(element.scrollHeight > element.clientHeight);
            element.classList.add('group-has-checked:line-clamp-none');
        };

        checkTruncation();

        const resizeObserver = new ResizeObserver(() => {
            checkTruncation();
        });

        resizeObserver.observe(element);

        return () => {
            resizeObserver.disconnect();
        };
    }, [children, isTruncated$]);

    return (
        <div className={cn('relative flex flex-col group', containerClassName)} {...props}>
            <div
                ref={containerRef}
                className={cn(
                    'overflow-hidden line-clamp-(--line-clamp) group-has-checked:line-clamp-none group-has-checked:max-h-[666]',
                    {
                        'sm:line-clamp-(--line-clamp-sm)': Boolean(breakpoints?.['sm']),
                        'md:line-clamp-(--line-clamp-md)': Boolean(breakpoints?.['md']),
                        'lg:line-clamp-(--line-clamp-lg)': Boolean(breakpoints?.['lg']),
                        'xl:line-clamp-(--line-clamp-xl)': Boolean(breakpoints?.['xl']),
                        '2xl:line-clamp-(--line-clamp-2xl)': Boolean(breakpoints?.['2xl']),
                    },
                    className
                )}
                style={getLineClampStyles(lines, breakpoints)}
            >
                {children}
            </div>
            <$React.label
                $className={() =>
                    cn(
                        'ml-auto text-sm underline cursor-pointer font-semibold',
                        {
                            invisible: !isTruncated$.get(),
                        },
                        readMoreClassName
                    )
                }
                tabIndex={0}
            >
                <input type="checkbox" className="peer invisible absolute" />
                <span className="inline peer-checked:hidden">{readMoreLabel}</span>
                <span className="hidden peer-checked:inline">{readLessLabel}</span>
            </$React.label>
        </div>
    );
}

function getLineClampStyles(lines: number, breakpoints?: Partial<Record<Breakpoint, number>>) {
    const style: Record<string, number> = { '--line-clamp': lines };
    if (breakpoints) {
        for (const [key, value] of Object.entries(breakpoints)) {
            if (value !== undefined) {
                style[`--line-clamp-${key}`] = value;
            }
        }
    }
    return style;
}
